import {PromptTarget} from '../state/useNovaPromptState';

export type NovaVariableScope = 'TASK' | 'PROJECT_CONTEXT' | 'FILES';
export type NovaPromptInput = 'SYSTEM_PROMPT' | 'USER_PROMPT_TEMPLATE' | 'AGENT_MD';
export type NovaVariablePrecedenceTier = 'INSTRUCTION' | 'PULLED_DATA';
export type NovaVariableLifecycleState = 'ACTIVE' | 'DEPRECATED' | 'REMOVED';

// The fields the library UI actually consumes. The registry serves more metadata (bound condition,
// visibility prose, volume handling, precedence tier, …) but none of it is shown, so it is not
// requested — keeping the fetched shape, and the non-null surface it has to be validated against,
// to only what is used.
export interface NovaVariable {
	token: string;
	scope: NovaVariableScope;
	description: string;
	lifecycleState: NovaVariableLifecycleState;
}

// Validators for the enum strings the registry returns. The registry is an externally-owned service,
// so a value outside the frozen set (e.g. a lifecycle state added upstream) is possible; these let
// the mapper drop or default such a value instead of trusting it blindly.
export function isNovaVariableScope(value: unknown): value is NovaVariableScope {
	return value === 'TASK' || value === 'PROJECT_CONTEXT' || value === 'FILES';
}

export function isNovaVariableLifecycleState(value: unknown): value is NovaVariableLifecycleState {
	return value === 'ACTIVE' || value === 'DEPRECATED' || value === 'REMOVED';
}

// Which authored input a given insertion target IS. Not a permission — every variable works in every
// input. This is how a grounding entry records the input it was found in.
export const PROMPT_TARGET_TO_INPUT: Record<PromptTarget, NovaPromptInput> = {
	[PromptTarget.System]: 'SYSTEM_PROMPT',
	[PromptTarget.User]: 'USER_PROMPT_TEMPLATE',
	[PromptTarget.AgentMd]: 'AGENT_MD',
};

// The name of each input as an insertion target, for the pill that tells the author where a clicked
// variable will land. Keyed by target so adding a target without a label is a compile error rather
// than a silently wrong label.
export const TARGET_LABEL_ID: Record<PromptTarget, string> = {
	[PromptTarget.System]: 'nova.variableLibrary.targetSystem',
	[PromptTarget.User]: 'nova.variableLibrary.targetUser',
	[PromptTarget.AgentMd]: 'nova.variableLibrary.targetAgentMd',
};

// The status that means an attached file is usable as a variable. PROCESSING/SUMMARISED are reserved
// for a later attach-time-summarisation upgrade; only READY files are offered today.
export const NOVA_FILE_READY_STATUS = 'READY';

export interface NovaAgentFileRecord {
	variableToken: string;
	filename: string;
	processingStatus: string;
}

// A citable {file:...} variable for an attached document.
export function fileToNovaVariable(file: NovaAgentFileRecord): NovaVariable {
	return {
		token: file.variableToken,
		scope: 'FILES',
		description: file.filename,
		lifecycleState: 'ACTIVE',
	};
}

export const SCOPE_ORDER: NovaVariableScope[] = ['TASK', 'PROJECT_CONTEXT', 'FILES'];

export interface NovaVariableGroup {
	scope: NovaVariableScope;
	variables: NovaVariable[];
}

export function groupVariablesByScope(variables: NovaVariable[]): NovaVariableGroup[] {
	return SCOPE_ORDER.map(scope => ({
		scope,
		variables: variables.filter(variable => variable.scope === scope),
	})).filter(group => group.variables.length > 0);
}
